import type { CatalogItem } from '~/api/types'
import { Badge, type BadgeTone } from '~/components/ui/badge'
import { Checkbox } from '~/components/ui/checkbox'
import { Pagination } from '~/components/ui/pagination'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '~/components/ui/table'
import type { TableSelection } from '~/lib/use-table-selection'
import { cn } from '~/lib/utils'

// 카탈로그 워크벤치 우측 결과 영역. 결과바 + 고밀도 항목 테이블(제목 2줄 말줄임,
// PART/분류/규모/데이터유형 배지, 결정형 표시) + 페이지네이션. 행 클릭 → 편집 드로어.

const DATA_TYPE_TONE: Record<string, BadgeTone> = {
    정량: 'info',
    정성: 'warning',
    boolean: 'info',
    number: 'accent',
    text: 'neutral',
    select: 'warning',
}
const SCALE_TONE: Record<string, BadgeTone> = {
    general: 'accent',
    small: 'info',
    medium: 'warning',
    large: 'accent',
}
const SCALE_LABEL: Record<string, string> = {
    general: '일반',
    small: '소규모',
}

export function ItemTable({
    items,
    loading,
    total,
    offset,
    pageSize,
    selectedId,
    selection,
    onSelect,
    onOffset,
}: {
    items: CatalogItem[]
    loading: boolean
    total: number
    offset: number
    pageSize: number
    selectedId?: string
    /** 다중선택 상태(상위에서 useTableSelection으로 관리). 없으면 체크박스 열을 숨긴다. */
    selection?: TableSelection<string>
    onSelect: (item: CatalogItem) => void
    onOffset: (offset: number) => void
}) {
    const from = total === 0 ? 0 : offset + 1
    const to = offset + items.length
    // 현재 페이지의 행 id — 전체선택 체크박스의 상태/토글 기준.
    const visibleIds = items.map((it) => it.id)
    const headState = selection?.allState(visibleIds) ?? 'none'
    const colSpan = selection ? 9 : 8

    return (
        <div className='space-y-3'>
            <div className='flex items-center justify-between'>
                <p className='text-muted-foreground text-sm'>
                    총{' '}
                    <span className='text-foreground font-bold'>
                        {total.toLocaleString()}
                    </span>
                    개 중{' '}
                    <span className='text-foreground font-bold'>
                        {from}–{to}
                    </span>
                    {loading ? (
                        <span className='ml-2 text-xs'>불러오는 중…</span>
                    ) : null}
                </p>
            </div>

            <Table>
                <TableHeader>
                    <TableRow>
                        {selection ? (
                            <TableHead className='w-9'>
                                <Checkbox
                                    aria-label='현재 페이지 전체 선택'
                                    checked={headState === 'all'}
                                    indeterminate={headState === 'some'}
                                    disabled={visibleIds.length === 0}
                                    onChange={() =>
                                        selection.toggleAll(visibleIds)
                                    }
                                />
                            </TableHead>
                        ) : null}
                        <TableHead className='w-12 text-right'>#</TableHead>
                        <TableHead>제목</TableHead>
                        <TableHead className='w-24'>PART</TableHead>
                        <TableHead className='w-24'>분류</TableHead>
                        <TableHead className='w-16'>규모</TableHead>
                        <TableHead className='w-14 text-right'>배점</TableHead>
                        <TableHead className='w-20'>데이터유형</TableHead>
                        <TableHead className='w-16'>결정형</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {items.length === 0 ? (
                        <TableRow>
                            <TableCell
                                colSpan={colSpan}
                                className='text-muted-foreground py-12 text-center'>
                                {loading
                                    ? '불러오는 중…'
                                    : '조건에 맞는 항목이 없어요. 필터를 조정해 보세요.'}
                            </TableCell>
                        </TableRow>
                    ) : (
                        items.map((it, i) => {
                            const selected = selectedId === it.id
                            const checked =
                                selection?.isSelected(it.id) ?? false
                            return (
                                <TableRow
                                    key={it.id}
                                    onClick={() => onSelect(it)}
                                    className={cn(
                                        'cursor-pointer',
                                        selected && 'bg-accent hover:bg-accent'
                                    )}>
                                    {selection ? (
                                        // 체크박스 클릭은 행 선택(편집 드로어)과 분리한다.
                                        <TableCell
                                            onClick={(e) =>
                                                e.stopPropagation()
                                            }>
                                            <Checkbox
                                                aria-label={`${it.title} 선택`}
                                                checked={checked}
                                                onChange={() =>
                                                    selection.toggle(it.id)
                                                }
                                            />
                                        </TableCell>
                                    ) : null}
                                    <TableCell className='text-muted-foreground text-right tabular-nums'>
                                        {offset + i + 1}
                                    </TableCell>
                                    <TableCell className='text-foreground max-w-md'>
                                        <span className='line-clamp-2'>
                                            {it.title}
                                        </span>
                                        {it.itemNumber ? (
                                            <span className='text-muted-foreground text-xs'>
                                                #{it.itemNumber}
                                            </span>
                                        ) : null}
                                    </TableCell>
                                    <TableCell>
                                        <Badge tone='neutral'>
                                            {it.part || '-'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <Badge
                                            tone={
                                                it.classification === '선행요건'
                                                    ? 'gray'
                                                    : 'primary'
                                            }>
                                            {it.classification || '-'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        {it.scale ? (
                                            <Badge
                                                tone={
                                                    SCALE_TONE[it.scale] ??
                                                    'neutral'
                                                }>
                                                {SCALE_LABEL[it.scale] ??
                                                    it.scale}
                                            </Badge>
                                        ) : (
                                            '-'
                                        )}
                                    </TableCell>
                                    <TableCell className='text-right tabular-nums'>
                                        {it.maxScore ?? '-'}
                                    </TableCell>
                                    <TableCell>
                                        {it.dataType ? (
                                            <Badge
                                                tone={
                                                    DATA_TYPE_TONE[
                                                        it.dataType
                                                    ] ?? 'neutral'
                                                }>
                                                {it.dataType}
                                            </Badge>
                                        ) : (
                                            '-'
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {it.isDeterministic ? (
                                            <Badge tone='info'>결정형</Badge>
                                        ) : (
                                            <span className='text-muted-foreground'>
                                                -
                                            </span>
                                        )}
                                    </TableCell>
                                </TableRow>
                            )
                        })
                    )}
                </TableBody>
            </Table>

            <Pagination
                total={total}
                pageSize={pageSize}
                offset={offset}
                onOffset={onOffset}
                disabled={loading}
            />
        </div>
    )
}
